<template>
  <teleport to="body">
    <transition-group
      name="notification"
      tag="div"
      class="base-notification-container"
    >
      <div
        v-for="notification in notifications"
        :key="notification.id"
        :class="[
          'base-notification',
          `base-notification--${notification.type}`,
          {
            'base-notification--dismissible': notification.dismissible
          }
        ]"
        role="alert"
        :aria-live="notification.type === 'error' ? 'assertive' : 'polite'"
      >
        <div class="base-notification__icon">
          <component :is="getIcon(notification.type)" />
        </div>

        <div class="base-notification__content">
          <h4 v-if="notification.title" class="base-notification__title">
            {{ notification.title }}
          </h4>
          <p class="base-notification__message">
            {{ notification.message }}
          </p>
          <div v-if="notification.actions" class="base-notification__actions">
            <button
              v-for="action in notification.actions"
              :key="action.label"
              :class="[
                'base-notification__action',
                `base-notification__action--${action.type || 'default'}`
              ]"
              @click="handleAction(notification, action)"
            >
              {{ action.label }}
            </button>
          </div>
        </div>

        <button
          v-if="notification.dismissible"
          class="base-notification__close"
          type="button"
          aria-label="Close notification"
          @click="dismiss(notification.id)"
        >
          <CloseIcon />
        </button>

        <div
          v-if="notification.duration && !notification.paused"
          class="base-notification__progress"
          :style="{ animationDuration: `${notification.duration}ms` }"
        ></div>
      </div>
    </transition-group>
  </teleport>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'

// Icon Components (inline for simplicity)
const SuccessIcon = {
  template: `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
    </svg>
  `
}

const ErrorIcon = {
  template: `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
    </svg>
  `
}

const WarningIcon = {
  template: `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
    </svg>
  `
}

const InfoIcon = {
  template: `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
    </svg>
  `
}

const CloseIcon = {
  template: `
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z" />
    </svg>
  `
}

export default defineComponent({
  name: 'BaseNotification',
  components: {
    SuccessIcon,
    ErrorIcon,
    WarningIcon,
    InfoIcon,
    CloseIcon
  },
  props: {
    notifications: {
      type: Array,
      default: () => []
    }
  },
  emits: ['dismiss', 'action'],
  setup(props, { emit }) {
    const getIcon = (type) => {
      const icons = {
        success: 'SuccessIcon',
        error: 'ErrorIcon',
        warning: 'WarningIcon',
        info: 'InfoIcon'
      }
      return icons[type] || 'InfoIcon'
    }

    const dismiss = (id) => {
      emit('dismiss', id)
    }

    const handleAction = (notification, action) => {
      emit('action', { notification, action })
      if (action.dismissOnClick !== false) {
        dismiss(notification.id)
      }
    }

    return {
      getIcon,
      dismiss,
      handleAction
    }
  }
})
</script>

<style scoped>
.base-notification-container {
  position: fixed;
  top: var(--space-4);
  right: var(--space-4);
  z-index: var(--z-tooltip);
  pointer-events: none;
  max-width: 400px;
  width: 100%;
}

.base-notification {
  position: relative;
  display: flex;
  gap: var(--space-3);
  padding: var(--space-4);
  margin-bottom: var(--space-3);
  background-color: var(--color-white);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  border-left: 4px solid transparent;
  pointer-events: auto;
  overflow: hidden;
  max-width: 100%;
}

/* Notification Types */
.base-notification--success {
  border-left-color: var(--color-success);
  background-color: #F0FDF4;
}

.base-notification--success .base-notification__icon {
  color: var(--color-success);
}

.base-notification--error {
  border-left-color: var(--color-error);
  background-color: #FEF2F2;
}

.base-notification--error .base-notification__icon {
  color: var(--color-error);
}

.base-notification--warning {
  border-left-color: var(--color-warning);
  background-color: #FFFBEB;
}

.base-notification--warning .base-notification__icon {
  color: var(--color-warning);
}

.base-notification--info {
  border-left-color: var(--color-info);
  background-color: #EFF6FF;
}

.base-notification--info .base-notification__icon {
  color: var(--color-info);
}

/* Icon */
.base-notification__icon {
  flex-shrink: 0;
  margin-top: var(--space-1);
}

/* Content */
.base-notification__content {
  flex: 1;
  min-width: 0;
}

.base-notification__title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-900);
  margin: 0;
  margin-bottom: var(--space-1);
  line-height: 1.4;
}

.base-notification__message {
  font-size: var(--font-size-sm);
  color: var(--color-gray-700);
  line-height: 1.5;
  margin: 0;
}

.base-notification__actions {
  display: flex;
  gap: var(--space-2);
  margin-top: var(--space-3);
}

.base-notification__action {
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  border: 1px solid transparent;
  border-radius: var(--radius-base);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.base-notification__action--default {
  background-color: var(--color-gray-100);
  color: var(--color-gray-700);
  border-color: var(--color-gray-300);
}

.base-notification__action--default:hover {
  background-color: var(--color-gray-200);
}

.base-notification__action--primary {
  background-color: var(--color-primary);
  color: var(--color-white);
  border-color: var(--color-primary);
}

.base-notification__action--primary:hover {
  background-color: var(--color-primary-dark);
}

/* Close Button */
.base-notification__close {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  background-color: transparent;
  color: var(--color-gray-400);
  border-radius: var(--radius-base);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.base-notification__close:hover {
  background-color: var(--color-gray-100);
  color: var(--color-gray-600);
}

/* Progress Bar */
.base-notification__progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background-color: currentColor;
  opacity: 0.3;
  animation: shrink linear;
  transform-origin: left center;
}

@keyframes shrink {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

/* Transitions */
.notification-enter-active {
  transition: all var(--transition-base);
}

.notification-leave-active {
  transition: all var(--transition-base);
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.notification-move {
  transition: transform var(--transition-base);
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .base-notification-container {
    top: var(--space-2);
    right: var(--space-2);
    left: var(--space-2);
    max-width: none;
  }

  .base-notification {
    padding: var(--space-3);
    margin-bottom: var(--space-2);
  }

  .base-notification__actions {
    flex-direction: column;
  }

  .base-notification__action {
    justify-content: center;
    text-align: center;
  }
}

/* Dark mode support (if needed) */
@media (prefers-color-scheme: dark) {
  .base-notification {
    background-color: var(--color-gray-800);
    color: var(--color-gray-100);
  }

  .base-notification__title {
    color: var(--color-gray-100);
  }

  .base-notification__message {
    color: var(--color-gray-300);
  }

  .base-notification--success {
    background-color: rgba(34, 197, 94, 0.1);
  }

  .base-notification--error {
    background-color: rgba(239, 68, 68, 0.1);
  }

  .base-notification--warning {
    background-color: rgba(245, 158, 11, 0.1);
  }

  .base-notification--info {
    background-color: rgba(59, 130, 246, 0.1);
  }
}

/* Hover pause functionality */
.base-notification:hover .base-notification__progress {
  animation-play-state: paused;
}

/* Accessibility improvements */
.base-notification:focus-within {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

/* Custom notification container positioning */
.base-notification-container--top-left {
  top: var(--space-4);
  left: var(--space-4);
  right: auto;
}

.base-notification-container--top-center {
  top: var(--space-4);
  left: 50%;
  right: auto;
  transform: translateX(-50%);
}

.base-notification-container--bottom-right {
  top: auto;
  bottom: var(--space-4);
  right: var(--space-4);
}

.base-notification-container--bottom-left {
  top: auto;
  bottom: var(--space-4);
  left: var(--space-4);
  right: auto;
}

.base-notification-container--bottom-center {
  top: auto;
  bottom: var(--space-4);
  left: 50%;
  right: auto;
  transform: translateX(-50%);
}
</style>